/**
 * Pure helpers for the "Re-order action" pebble (Order history & account).
 *
 * Extracted from StorefrontOrderDetail.vue so they can be unit-tested under
 * vitest (which can't mount Vuetify 3 SFCs in this project — see
 * project_vitest-no-plugin-vue.md).
 *
 * Re-order takes an order's existing line items and re-adds each one to the
 * shopper's current cart via the canonical
 * /accounts/<id>/update_cart_quantity endpoint
 * (api/src/models/account.py:762 — `update_cart_quantity`). The component
 * loops items, posts each one independently, then uses
 * summarizeReorderResult() to decide what feedback to show.
 */
import { artworkKey } from "./cartLinePrices";

/**
 * Shape an order_item into the args expected by update_cart_quantity.
 *
 * The four fields below are direct attributes of OrderItem
 * (api/src/models/order_item.py:18-22) and are also the four args accepted
 * by Account.update_cart_quantity (api/src/models/account.py:762).
 *
 * Returns null when the item is missing fields required by the backend
 * (campaign_product_id or a positive quantity). Empty/null
 * primary/secondary attribute values are valid — products without variants
 * use those.
 *
 * Defensively coerces quantity to a number — JSON:API sometimes serializes
 * Integer columns as strings in mixed payloads.
 *
 * @param {object|null|undefined} orderItem
 * @returns {{campaign_product_id: number, primary_attribute_value: string|null,
 *            secondary_attribute_value: string|null, quantity: number}|null}
 */
export function buildReorderArgs(orderItem) {
  if (!orderItem || typeof orderItem !== "object") return null;

  const campaignProductId = orderItem.campaign_product_id;
  if (campaignProductId == null) return null;

  const quantityRaw = orderItem.quantity;
  if (quantityRaw == null) return null;
  const quantity = Number(quantityRaw);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;

  // Empty string and null are both valid (no-variant products). We pass
  // them through as-is so the backend sees the same shape as on original
  // checkout.
  const primary =
    orderItem.primary_attribute_value === undefined
      ? null
      : orderItem.primary_attribute_value;
  const secondary =
    orderItem.secondary_attribute_value === undefined
      ? null
      : orderItem.secondary_attribute_value;

  return {
    campaign_product_id: campaignProductId,
    primary_attribute_value: primary,
    secondary_attribute_value: secondary,
    quantity,
  };
}

/**
 * Compute the quantity that should be sent to update_cart_quantity for an
 * order item, given the current cart state.
 *
 * Why this exists: the backend's update_cart_quantity is ABSOLUTE, not
 * additive — passing `quantity=3` sets the cart line to 3, overwriting any
 * existing quantity. The canonical add-to-cart flow in
 * StorefrontView.vue:747-756 compensates by summing the existing matching
 * cart quantity with the incoming quantity BEFORE POSTing. Re-order has to
 * mirror that pattern, otherwise re-ordering an item already in the cart
 * silently overwrites the shopper's existing quantity.
 *
 * Matching key is the one the backend uses to identify a cart line:
 * (campaign_product_id, primary_attribute_value, secondary_attribute_value)
 * plus the logo. A SUPPLIED logo must match the line's logo -- two lines
 * differing only by logo are two lines (``_artwork_key`` in
 * api/src/models/account.py), so re-ordering logo A must not absorb logo B's
 * quantity. An absent logo keeps the original any-logo match, mirroring the
 * backend's wildcard for an absent ``selected_artwork_id``.
 *
 * Returns 0 for invalid input (null/undefined orderItem, missing quantity)
 * — the caller's buildReorderArgs() will then reject the line as invalid,
 * which is the correct behavior.
 *
 * @param {object|null|undefined} orderItem - the order_item being re-ordered
 * @param {Array<object>|null|undefined} currentCart - this store's cart (store_cart)
 * @param {number|string|null} [selectedArtworkId] - the logo being sent
 * @returns {number} quantity to send to update_cart_quantity
 */
export function mergedQuantityForCart(
  orderItem,
  currentCart,
  selectedArtworkId = null,
) {
  if (!orderItem || typeof orderItem !== "object") return 0;
  const orderQty = Number(orderItem.quantity);
  if (!Number.isFinite(orderQty) || orderQty <= 0) return 0;

  const cart = Array.isArray(currentCart) ? currentCart : [];
  const matchingItems = cart.filter(
    (c) =>
      c &&
      c.campaign_product_id === orderItem.campaign_product_id &&
      c.primary_attribute_value === orderItem.primary_attribute_value &&
      c.secondary_attribute_value === orderItem.secondary_attribute_value &&
      (selectedArtworkId == null ||
        artworkKey(c.selected_artwork_id) === artworkKey(selectedArtworkId)),
  );

  const existingQty = matchingItems.reduce((sum, c) => {
    const q = Number(c.quantity);
    return Number.isFinite(q) ? sum + q : sum;
  }, 0);

  return existingQty + orderQty;
}

/**
 * Decide which logo a re-ordered line carries, or why it cannot be added.
 *
 * The order froze the logo the garment was made with on
 * ``order_items.resolved_artwork_id`` at checkout (the shopper's pick, or the
 * server's own when the color offered one). Re-order sends it back as the
 * cart line's ``selected_artwork_id``; without it the line came back with no
 * logo, and checkout then re-derived one the shopper may never have chosen.
 *
 * ``logosByColor`` is the ``logos`` map of the storefront product payload
 * (GET /storefront/<slug>/products/<id>): the logos each color offers TODAY.
 * The same rules as the PDP picker (ProductPage.vue):
 *   - the order's logo is still offered        -> keep it
 *   - the order's logo is no longer offered    -> refuse the line. Never add
 *     it with no logo, and never swap in another logo, even the only one
 *     left: either puts something on the garment the shopper did not choose.
 *   - the order recorded no logo, and the color offers none -> no logo
 *   - ... and exactly one -> that one (the PDP preselects it)
 *   - ... and several     -> refuse: the PDP blocks the add until a pick.
 *
 * @param {object} orderItem
 * @param {object|null|undefined} logosByColor - color id -> [{id, ...}]
 * @returns {{ok: true, selectedArtworkId: number|string|null}
 *          |{ok: false, reason: "logo-unavailable"|"logo-choice-needed"}}
 */
export function reorderLogoChoice(orderItem, logosByColor) {
  const color = orderItem ? orderItem.primary_attribute_value : null;
  const entries =
    logosByColor && color != null ? logosByColor[color] : undefined;
  const offered = Array.isArray(entries) ? entries : [];

  const frozen = orderItem ? artworkKey(orderItem.resolved_artwork_id) : null;
  if (frozen !== null) {
    const match = offered.find((logo) => artworkKey(logo && logo.id) === frozen);
    return match
      ? { ok: true, selectedArtworkId: match.id }
      : { ok: false, reason: "logo-unavailable" };
  }
  if (offered.length === 0) return { ok: true, selectedArtworkId: null };
  if (offered.length === 1) return { ok: true, selectedArtworkId: offered[0].id };
  return { ok: false, reason: "logo-choice-needed" };
}

/**
 * The shopper-facing reason a line was not re-ordered for its logo.
 *
 * @param {string} name - the product name
 * @param {string} reason - a ``reorderLogoChoice`` refusal reason
 * @returns {string}
 */
export function reorderLogoMessage(name, reason) {
  if (reason === "logo-unavailable") {
    return `Could not re-order ${name}: its logo is no longer available. Choose a logo on the product page.`;
  }
  return `Could not re-order ${name}: choose a logo on the product page.`;
}

/**
 * Pluralize "item" / "items" based on count.
 *
 * @param {number} n
 * @returns {string}
 */
function itemWord(n) {
  return n === 1 ? "item" : "items";
}

/**
 * Summarize the outcome of a re-order loop.
 *
 * Takes an array of per-item results — `{success: bool, name: string, error?: string}` —
 * and returns the UX decision payload:
 *   - successCount / errorCount: integer counts
 *   - navigateToCart: should we navigate to /cart after dispatching feedback?
 *                     true if AT LEAST one item succeeded.
 *   - summaryMessage: a single-line summary suitable for raiseSuccess /
 *                     raiseError. Per-item error feedback is dispatched
 *                     separately by the caller in-loop.
 *
 * @param {Array<{success: boolean, name?: string, error?: string}>|null|undefined} results
 * @returns {{successCount: number, errorCount: number,
 *            navigateToCart: boolean, summaryMessage: string}}
 */
export function summarizeReorderResult(results) {
  const list = Array.isArray(results) ? results : [];

  const successCount = list.filter((r) => r && r.success).length;
  const errorCount = list.filter((r) => r && !r.success).length;
  const total = successCount + errorCount;

  if (total === 0) {
    return {
      successCount: 0,
      errorCount: 0,
      navigateToCart: false,
      summaryMessage: "No items to re-order",
    };
  }

  if (errorCount === 0) {
    return {
      successCount,
      errorCount: 0,
      navigateToCart: true,
      summaryMessage: `Added ${successCount} ${itemWord(successCount)} to cart`,
    };
  }

  if (successCount === 0) {
    return {
      successCount: 0,
      errorCount,
      navigateToCart: false,
      summaryMessage: "Could not add any items to cart",
    };
  }

  return {
    successCount,
    errorCount,
    navigateToCart: true,
    summaryMessage: `Added ${successCount} of ${total} items; ${errorCount} unavailable`,
  };
}
